import { Globe, Mail, MessageSquare, Phone, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { CapabilityGate } from '../../components/CapabilityGate';
import { UserAvatar } from '../../components/UserAvatar';
import { useCapability } from '../../hooks/use-capability';
import { SensitivityBadge } from '../conversations/components/SensitivityBadge';
import { isTerminalStatus, relativeTime } from '../conversations/format';

import {
  AwaitingIndicator,
  BondBadge,
  CommitmentBrokenBadge,
  GroupIndicator,
  InboxModeBadge,
  InboxSequenceBadge,
  IntentBadge,
  MetaBadge,
  QueueHeldBadge,
  TransferIndicator,
  UrgencyIndicator,
} from './inbox-badges';

import type { ConversationListItem } from '../../lib/types';
import type { ChannelKind } from '@lume/shared';

/**
 * Prefixo do preview segue a ORIGEM da última fala, não só a direção (A-09,
 * noturna 04/Set/2026): em H_c a última fala é da IA e «Você:» atribuía ao
 * operador o que a IA disse.
 */
function previewPrefix(last: ConversationListItem['lastMessage']): string {
  switch (last?.origin) {
    case 'ai':
      return 'IA: ';
    case 'system':
      return 'Sistema: ';
    default:
      return 'Você: ';
  }
}

/**
 * ConversationCard2B — card de conversa rico (4 linhas) da Tela 1 Inbox
 * (Brief 2B §6.4). Distinto do ConversationCard de Brief 2A (que permanece
 * compartilhado e intocado).
 *
 * Consome os campos de enriquecimento B3 do `ConversationListItem`
 * (PR #304) e as classes JÁ materializadas em inbox-2b.css. Honest-defer
 * onde não há fonte de dado no list item (vide comentários inline) — NÃO
 * inventa: team, adversarial, origin CTWA, unread, proactive, custo/expira
 * Meta, nome do remetente da transferência.
 */
export function ConversationCard2B({
  conversation,
  selected = false,
  onSelect,
  onClaim,
  compact = false,
}: {
  conversation: ConversationListItem;
  selected?: boolean;
  onSelect?: (id: string) => void;
  /**
   * B1 (fricção medida no teste do TL, 12/Ago): dentro do grupo por pessoa a
   * identidade vive no CABEÇALHO do grupo — repetir avatar+nome+canal em cada
   * card mantinha o «nome listado várias vezes» que a frente existe para
   * eliminar. `compact` suprime avatar e a linha 1 de identidade; preview,
   * status, hora e sinais por conversa ficam intactos.
   */
  compact?: boolean;
  /**
   * Lote Claim — quando a conversa não tem dono (`assignedToUserId == null`),
   * o card oferece "Pegar conversa" em vez de só navegar. Atendente não tem
   * acesso de leitura à conversa órfã (403 not-assigned), então abrir o detalhe
   * direto cairia no redirect do Item 88 sem alcançar o ClaimBanner. O pai wira
   * claim → navega ao detalhe já como dono. (Decisão via humano, 14/Jun/2026.)
   */
  onClaim?: (id: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const canViewPerson = useCapability('person-read');
  const c = conversation;
  const isUnassigned = c.assignedToUserId === null || c.assignedToUserId === undefined;
  const claimable = isUnassigned && onClaim !== undefined;
  const personName = c.person?.displayName ?? 'Sem identificação';
  // Deep-link para a Tela 2 (Pessoas) — só quando há personId + cap person-read.
  const personId = c.personId;
  const personLinkable = canViewPerson && personId !== null;
  const isUrgent = c.operationalPriority === 'p0' || c.operationalPriority === 'p1';
  const isHc = c.operationMode.current === 'H_c';
  const isClosed = isTerminalStatus(c.status);
  const awaiting = c.awaitingPartyType;
  const isOutbound = c.lastMessage?.direction === 'outbound';
  // Spec fila §D7 — fala outbound que NÃO chegou ao cliente não pode parecer entregue.
  const lastFailed = isOutbound && c.lastMessage?.status === 'failed';

  // honest-defer overdue: sem flag no contrato → derivação simples (operador
  // aguardando > 60min). Evita afirmar overdue para o cliente, onde não há SLA.
  const awaitingOverdue =
    awaiting === 'operator' &&
    c.awaitingDurationMinutes !== null &&
    c.awaitingDurationMinutes !== undefined &&
    c.awaitingDurationMinutes > 60;

  // honest-defer --unread / --proactive: sem flag no list item → não aplicados.
  const cardCls = [
    'convo-card-2b',
    selected && 'convo-card-2b--selected',
    isUrgent && 'convo-card-2b--p1',
    isHc && 'convo-card-2b--hc',
    isClosed && 'convo-card-2b--closed',
  ]
    .filter(Boolean)
    .join(' ');

  const handleSelect = (): void => {
    // Conversa órfã: o clique do corpo também "pega" (claim) em vez de abrir o
    // detalhe — atendente não lê órfã (403), abrir cairia no redirect. O botão
    // explícito reforça a ação; ambos chamam onClaim.
    if (claimable && onClaim !== undefined) {
      onClaim(c.id);
      return;
    }
    if (onSelect !== undefined) onSelect(c.id);
  };

  return (
    <div
      className={cardCls}
      role="button"
      tabIndex={0}
      data-conv-id={c.id}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
    >
      {!compact && (
        <div className="convo-card-2b__avatar">
          <UserAvatar name={personName} colorKey={c.personId ?? c.id} size="md" />
        </div>
      )}

      <div className="convo-card-2b__body">
        {/* Linha 1 — identidade (suprimida no modo compact: vive no grupo). */}
        {!compact && (
          <div className="convo-card-2b__head">
            {personLinkable ? (
              <span
                className="convo-card-2b__name"
                role="link"
                tabIndex={0}
                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                title="Ver ficha da pessoa"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/pessoas/${personId}`);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    navigate(`/pessoas/${personId}`);
                  }
                }}
              >
                {personName}
              </span>
            ) : (
              <span className="convo-card-2b__name">{personName}</span>
            )}
            <span className="convo-card-2b__head-sep" aria-hidden />
            <span className="convo-card-2b__channel">
              <ChannelIcon channel={c.channel} />
              {channelShortLabel(c.channel)}
            </span>
            {/* honest-defer team: só assignedTeamId (sem nome) → __team omitido. */}

            <span className="convo-card-2b__head-trail">
              {c.transfer != null && <TransferIndicator minutesAgo={c.transfer.minutesAgo} />}
              {/* honest-defer adversarial: sem flag no list item → omitido. */}
            </span>
          </div>
        )}

        {/* Linha 2 — preview */}
        <div
          className={`convo-card-2b__preview${lastFailed ? ' convo-card-2b__preview--failed' : ''}`}
        >
          {lastFailed && (
            <span className="convo-card-2b__preview-failed-tag">⚠ Não entregue: </span>
          )}
          {isOutbound && !lastFailed && (
            <span className="convo-card-2b__preview-prefix">{previewPrefix(c.lastMessage)}</span>
          )}
          {c.lastMessage?.content ?? 'Sem mensagens ainda.'}
        </div>

        {/* Linha 3 — badges (renderiza só quando há dado) */}
        <div className="convo-card-2b__badges">
          {/* honest-defer intentState: backend retorna null hoje → não renderiza. */}
          {c.intentState !== null && <IntentBadge state={c.intentState} />}
          <CapabilityGate capability="inbox.filter.mode">
            <InboxModeBadge mode={c.operationMode.current} />
          </CapabilityGate>
          {/* honest-defer custo/expira Meta: metaSessionIsExpired não confiável. */}
          {c.metaSessionType !== null && <MetaBadge type={c.metaSessionType} />}
          {c.bond != null && <BondBadge type={c.bond.type} />}
          {c.sequence != null && (
            <InboxSequenceBadge total={c.sequence.total} state={c.sequence.state} />
          )}
          {c.group !== null && c.group !== undefined && c.group.count > 0 && (
            <GroupIndicator count={c.group.count} />
          )}
          {/* honest-defer origin CTWA: sem dado origin no list item → omitido. */}
          {c.commitment?.broken === true && <CommitmentBrokenBadge />}
          {/* S7 PR2 — escalação em aberto (modo recepcionista S4, gated). Rodada 3
              Task 4: o badge lê o operationMode ATUAL, não só o escalationState —
              «IA segurando» só é verdade em H_c; em H_a (proibida de falar) o
              badge honesto é «Aguardando humano». */}
          {c.escalationState === 'awaiting_claim' && (
            <QueueHeldBadge mode={c.operationMode?.current ?? ''} />
          )}
          <SensitivityBadge level={c.classificationLevel as 1 | 2 | 3 | 4 | null} />
          {isUrgent && <UrgencyIndicator />}
        </div>

        {/* Linha 4 — awaiting + time */}
        <div
          className={
            'convo-card-2b__foot' + (awaitingOverdue ? ' convo-card-2b__foot--overdue' : '')
          }
        >
          {(awaiting === 'customer' || awaiting === 'operator') && (
            <AwaitingIndicator
              party={awaiting}
              durationMinutes={c.awaitingDurationMinutes ?? null}
              overdue={awaitingOverdue}
            />
          )}
          {isClosed && awaiting !== 'customer' && awaiting !== 'operator' && (
            <span className="muted">Concluída</span>
          )}
          {claimable && !isClosed && (
            <button
              type="button"
              className="btn btn--primary btn--sm convo-card-2b__claim"
              data-testid="claim-conversation"
              onClick={(e) => {
                e.stopPropagation();
                if (onClaim !== undefined) onClaim(c.id);
              }}
            >
              Pegar conversa
            </button>
          )}
          {/* Rodada 3 Task 4 — updatedAt é proxy de espera p/ o sort de urgência do
              engine (orderByForSort, ASC) e NÃO pode ser bumpado no inbound; a hora
              exibida por isso vem da última mensagem, não do carimbo de sort. */}
          <span className="convo-card-2b__time">
            {relativeTime(c.lastMessage?.sentAt ?? c.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Channel helpers ────────────────────────────────────────────────────────

function ChannelIcon({ channel }: { channel: ChannelKind }): JSX.Element {
  switch (channel) {
    case 'whatsapp-cloud-api':
      return <Phone size={11} aria-hidden />;
    case 'webchat':
      return <Globe size={11} aria-hidden />;
    case 'email':
      return <Mail size={11} aria-hidden />;
    case 'telegram':
      return <Send size={11} aria-hidden />;
    default:
      return <MessageSquare size={11} aria-hidden />;
  }
}

const CHANNEL_SHORT: Record<ChannelKind, string> = {
  'whatsapp-cloud-api': 'WhatsApp',
  webchat: 'Webchat',
  email: 'Email',
  telegram: 'Telegram',
  'instagram-dm': 'Instagram',
  simulador: 'Simulador',
};

function channelShortLabel(channel: ChannelKind): string {
  return CHANNEL_SHORT[channel];
}
